import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './ProductCard.css';
import KOKO from '../../../assests/ProductCard/Koko.png';
import Mintpay from '../../../assests/ProductCard/Mintpay.png';
import { toggleWishlistProduct } from '../../../slices/wishlistSlice';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { Image_URL } from '../../../config/config';
import ProductViewModal from '../modalComponent/productModal/index';
import { useSalePage } from '../../../pages/Sales/SalePageContext';

const ProductCard = ({ product, onAddToCart, onMainImageClick }) => {
  const { isSaleEnabled } = useSalePage();
  const [selectedImages, setSelectedImages] = useState({});
  const [mainImage, setMainImage] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductColor, setSelectedProductColor] = useState(null);

  const wishlistItems = useSelector((state) => state.wishlist?.wishlistItems || []);
  const wishlistSlugs = useSelector((state) => state.wishlist?.wishlistSlugs || []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { formatPrice, getCurrencySymbol, isSriLanka } = useCurrency();

  const {
    ProductId,
    Price,
    InternationalPrice,
    Title,
    Colors,
    Slug,
    OfferPrice,
    InternationalOfferPrice
  } = product;

  // Memoized calculations
  const uniqueColors = useMemo(() => {
    const seenColors = new Set();
    return Colors?.reduce((acc, color) => {
      if (!seenColors.has(color.Color)) {
        seenColors.add(color.Color);
        acc.push(color);
      }
      return acc;
    }, []) || [];
  }, [Colors]);

  const { displayPrice, displayOfferPrice, installmentPrice, currencySymbol } = useMemo(() => {
    const actualPrice = isSriLanka() ? Price : (InternationalPrice || Price);
    const actualOfferPrice = isSriLanka()
      ? OfferPrice
      : (InternationalOfferPrice || OfferPrice);

    const symbol = getCurrencySymbol();
    const basePrice = isSaleEnabled && actualOfferPrice ? actualOfferPrice : actualPrice;
    
    return {
      displayPrice: `${symbol} ${parseFloat(actualPrice).toFixed(2)}`,
      displayOfferPrice: actualOfferPrice 
        ? `${symbol} ${parseFloat(actualOfferPrice).toFixed(2)}`
        : null,
      installmentPrice: Math.floor(parseFloat(basePrice) / 3),
      currencySymbol: symbol
    };
  }, [Price, InternationalPrice, OfferPrice, InternationalOfferPrice, isSaleEnabled, isSriLanka, getCurrencySymbol]);

  const imageDetails = useMemo(() => {
    const firstColorSources = Colors?.[0]?.Sources?.split(',') || [];
    return {
      currentMainImage: mainImage[ProductId] || firstColorSources[0] || "",
      currentSelectedImages: selectedImages[ProductId] || firstColorSources
    };
  }, [Colors, ProductId, mainImage, selectedImages]);

  // Handlers
  const handleColorClick = useCallback((productId, color) => {
    if (!color?.Sources) return;

    const sources = color.Sources.split(",").map((source) => source.trim());
    setSelectedImages((prev) => ({ ...prev, [productId]: sources }));
    setMainImage((prev) => ({ ...prev, [productId]: sources[0] }));
    setSelectedProductColor(color);
  }, []);

  const handleSmallImageClick = useCallback((productId, source) => {
    setMainImage((prev) => ({ ...prev, [productId]: source }));
  }, []);

  const handleMainImageClick = useCallback(() => {
    if (onMainImageClick) {
      onMainImageClick(Slug);
    } else {
      navigate(`/product/${Slug}`);
    }
  }, [onMainImageClick, navigate, Slug]);

  const toggleWishlist = useCallback(() => {
    if (!product) return;
    
    const wishlistPayload = {
      ProductId: product.ProductId,
      Title: product.Title,
      Price: isSriLanka() ? product.Price : (product.InternationalPrice || product.Price),
      Size: null,
      Color: null,
      Image: imageDetails.currentMainImage,
    };
    
    dispatch(toggleWishlistProduct(wishlistPayload));
  }, [dispatch, product, imageDetails.currentMainImage, isSriLanka]);

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  if (!product) return null;

  return (
    <>
      <div className="product-card-container my-3" key={`${ProductId}-${Slug}`}>
        <div className="product-card">
          <div className="product-card-body">
            {/* Main Image and Wishlist Icon */}
            <div className="product-main-image-container">
              {imageDetails.currentMainImage && (
                <img
                  src={`${Image_URL}products/thumbnails/${imageDetails.currentMainImage}`}
                  className="product-main-image"
                  alt={Title}
                  onClick={handleMainImageClick}
                  onError={(e) => {
                    e.target.src = "https://jbadmin.alephaz.com/assets/img/placeholder-image.jpg";
                  }}
                />
              )}
              <div className="heart-icon-container" onClick={toggleWishlist}>
                <i
                  className={`fa-${wishlistItems.some(item => item.ProductId === ProductId) ? 'solid' : 'regular'} fa-heart`}
                  style={{ color: wishlistItems.some(item => item.ProductId === ProductId) ? "red" : undefined }}
                  aria-label={`${wishlistItems.some(item => item.ProductId === ProductId) ? 'Remove from' : 'Add to'} wishlist`}
                />
              </div>
            </div>

            {/* Small Thumbnails */}
            <div className={`product-small-images-container ${
              imageDetails.currentSelectedImages.length === 2 ? "two-images" : ""
            }`}>
              {imageDetails.currentSelectedImages.slice(0, 3).map((source, index) => (
                <button
                  key={index}
                  className="product-small-image-wrapper"
                  onClick={() => handleSmallImageClick(ProductId, source)}
                  aria-label={`View ${Title} image ${index + 1}`}
                >
                  <img
                    src={`${Image_URL}products/thumbnails/${source.trim()}`}
                    className="product-small-image"
                    alt={`${Title} view ${index + 1}`}
                    onError={(e) => {
                      e.target.src = "https://jbadmin.alephaz.com/assets/img/placeholder-image.jpg";
                    }}
                  />
                </button>
              ))}
            </div>

            {/* Color Dots */}
            <div className="product-color-dots-container">
              {uniqueColors.slice(0, 4).map((color, index) => (
                <button
                  key={index}
                  className={`product-color-dot ${
                    selectedImages[ProductId]?.includes(color.Color) ? "selected" : ""
                  }`}
                  style={{ backgroundColor: color.Color }}
                  title={color.Color}
                  onClick={() => handleColorClick(ProductId, color)}
                  aria-label={`Select ${color.Color} color`}
                />
              ))}
            </div>

            {/* Product Details and Add to Cart */}
            <div className="product-text-and-button-container">
              <div className="product-text-container">
                <p className="product-name" onClick={handleMainImageClick}>
                  {Title}
                </p>
                <div>
                  <h5 className="product-price">
                    {isSaleEnabled && displayOfferPrice ? (
                      <>
                        <span className="product-sale-price">
                          {displayOfferPrice}
                        </span>
                        <span className="product-original-price">
                          {displayPrice}
                        </span>
                      </>
                    ) : (
                      <span className="product-highlighted-price">
                        {displayPrice}
                      </span>
                    )}
                  </h5>
                  <p className="product-installments">
                    or 3 installments of {currencySymbol}
                    {installmentPrice.toFixed(2)} with
                  </p>
                  <div className="payment-logos-container">
                    <img
                      src={Mintpay}
                      alt="Pay with MintPay"
                      className="product-payment-logo me-2"
                    />
                    <p className="product-installments">or</p>
                    <img
                      src={KOKO}
                      alt="Pay with Koko"
                      className="product-payment-logo"
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="btn product-main-button"
                onClick={handleModalOpen}
                aria-label={`Quick view ${Title}`}
              >
                <i className="fa-solid fa-cart-shopping" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && ReactDOM.createPortal(
        <ProductViewModal
          slug={Slug}
          preSelectedColor={selectedProductColor?.Color || Colors?.[0]?.Color || ""}
          preSelectedSize={selectedProductColor?.Size || Colors?.[0]?.Size || ""}
          onClose={handleModalClose}
        />,
        document.getElementById('modal-root')
      )}
    </>
  );
};

ProductCard.propTypes = {
  product: PropTypes.shape({
    ProductId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    Price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    InternationalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    OfferPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    InternationalOfferPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Title: PropTypes.string.isRequired,
    Colors: PropTypes.arrayOf(
      PropTypes.shape({
        Color: PropTypes.string.isRequired,
        Sources: PropTypes.string.isRequired,
        Size: PropTypes.string,
        SizeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        ColorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        PCID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ).isRequired,
    Slug: PropTypes.string.isRequired,
    CategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    SubCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Description: PropTypes.string,
  }).isRequired,
  onAddToCart: PropTypes.func.isRequired,
  onMainImageClick: PropTypes.func,
};

export default ProductCard;